import React, { useState, useMemo } from 'react';

const FAQ = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedItems, setExpandedItems] = useState(new Set());

  const faqData = [
    {
      category: "💼 GENERAL QUESTIONS",
      questions: [
        {
          id: 1,
          question: "What services do you offer?",
          answer: "We provide a wide range of services including custom API development, web performance optimization, application maintenance, automation workflows, and AI-powered business agents for calls, emails, and more."
        },
        {
          id: 2,
          question: "How are Krishna API Hub and OptimizPro related?",
          answer: "They've now been unified under one platform to deliver both robust backend infrastructure and intelligent AI agents in a single solution."
        },
        {
          id: 3,
          question: "Who are your ideal clients?",
          answer: "Our clients range from startups to enterprises across industries like eCommerce, SaaS, customer service, real estate, healthcare, and more."
        },
        {
          id: 4,
          question: "What industries do you serve?",
          answer: "We serve tech, healthcare, real estate, logistics, eCommerce, legal, and financial services—essentially any business looking to automate and scale."
        },
        {
          id: 5,
          question: "Where are you based?",
          answer: "We're based in India, with clients and partners globally."
        },
        {
          id: 6,
          question: "How do I get started?",
          answer: "Simply contact us through the website. We'll assess your needs and suggest a custom plan."
        },
        {
          id: 7,
          question: "Can I schedule a demo?",
          answer: "Yes, absolutely! Visit our contact page."
        },
        {
          id: 8,
          question: "Do you offer a free trial or pilot project?",
          answer: "Yes, we offer pilot projects or proof-of-concept builds in most cases."
        },
        {
          id: 9,
          question: "What makes you different from other AI or API providers?",
          answer: "We combine deep technical backend/API development experience with real-world AI automation. We build both the tools and the intelligence."
        },
        {
          id: 10,
          question: "Do you offer white-label solutions?",
          answer: "Yes, white-labeling options are available for agencies, resellers, and enterprise clients. The specifics depend on the scope of the project and the agreement terms. Please contact us to discuss your requirements."
        }
      ]
    },
    {
      category: "🧠 AI AGENTS / AUTOMATION",
      questions: [
        {
          id: 11,
          question: "What is an AI agent?",
          answer: "An AI agent is a software program powered by machine learning that can handle tasks like calls, emails, messages, or web chats automatically."
        },
        {
          id: 12,
          question: "What tasks can your AI agents automate?",
          answer: "Inbound/outbound calls, lead qualification, appointment setting, email inbox management, knowledge base responses, and more."
        },
        {
          id: 13,
          question: "Can your AI agents handle inbound and outbound calls?",
          answer: "Yes, our agents can receive calls, respond with context, and also make outbound sales or follow-up calls."
        },
        {
          id: 14,
          question: "How do AI email assistants work?",
          answer: "They connect to your inbox, analyze emails, and respond based on context, templates, or knowledge base."
        },
        {
          id: 15,
          question: "Can the AI agents be trained on my business data?",
          answer: "Yes, we use your documents, past messages, and FAQs to train the agents."
        },
        {
          id: 16,
          question: "How accurate are your AI agents?",
          answer: "Accuracy improves over time with feedback. We typically achieve 85–95% task accuracy depending on the use case."
        },
        {
          id: 17,
          question: "How do you measure AI agent performance?",
          answer: "Through response accuracy, task completion rate, average resolution time, and customer feedback scores."
        },
        {
          id: 18,
          question: "Can AI agents integrate with my CRM or ERP?",
          answer: "Yes, we offer integrations with most major tools or build custom connectors."
        },
        {
          id: 19,
          question: "Is there a limit to the number of conversations or calls?",
          answer: "Limits depend on your selected plan. Enterprise plans offer high-volume support."
        },
        {
          id: 20,
          question: "Can I use multiple agents for different teams?",
          answer: "Yes, you can deploy multiple specialized agents for different departments."
        },
        {
          id: 21,
          question: "How do I update or retrain the AI agents?",
          answer: "You can update data sources, or we can schedule regular retraining cycles."
        },
        {
          id: 22,
          question: "How fast is the response time for AI agents?",
          answer: "Responses are typically instant—within milliseconds to a few seconds."
        },
        {
          id: 23,
          question: "Can your AI generate leads automatically?",
          answer: "Yes, we offer lead generation and qualification agents for inbound and outbound campaigns."
        },
        {
          id: 24,
          question: "Will the AI replace my team?",
          answer: "No. It enhances productivity by automating repetitive tasks so your team can focus on strategy and human-touch roles."
        },
        {
          id: 25,
          question: "Is your AI multilingual?",
          answer: "Yes, we support multiple languages and can train agents accordingly."
        }
      ]
    },
    {
      category: "🔧 TECHNICAL SERVICES",
      questions: [
        {
          id: 26,
          question: "What are custom APIs and why would I need them?",
          answer: "Custom APIs allow your systems to communicate securely and efficiently, tailored to your exact business logic."
        },
        {
          id: 27,
          question: "Do you help with API integration into third-party platforms?",
          answer: "Yes, we build and integrate APIs into platforms like Shopify, Salesforce, Zoho, etc."
        },
        {
          id: 28,
          question: "Can you improve the performance of an existing web application?",
          answer: "Yes, we specialize in identifying bottlenecks and optimizing loading speed, latency, and server performance."
        },
        {
          id: 29,
          question: "What kind of batch jobs and metrics do you set up?",
          answer: "Scheduled data processing tasks, automated reports, cleanup jobs, and more—monitored via dashboards or logs."
        },
        {
          id: 30,
          question: "Do you offer application maintenance and support?",
          answer: "Yes, we offer ongoing support, updates, bug fixes, and system monitoring."
        },
        {
          id: 31,
          question: "Can you automate my business workflows?",
          answer: "Absolutely! We specialize in end-to-end workflow automation using custom logic and AI agents."
        },
        {
          id: 32,
          question: "Do you build real-time notification systems?",
          answer: "Yes, including push notifications, SMS alerts, webhook systems, and more."
        },
        {
          id: 33,
          question: "Can I see examples of performance improvements you've done?",
          answer: "Yes, we can share anonymized case studies during the consultation."
        },
        {
          id: 34,
          question: "What languages and frameworks do you use?",
          answer: "We use Python, Node.js, Java, React, Flask, FastAPI, MongoDB, PostgreSQL, and more."
        },
        {
          id: 35,
          question: "Do you work with cloud providers (AWS, Azure, GCP)?",
          answer: "Yes, we deploy and manage projects on all major cloud platforms."
        },
        {
          id: 36,
          question: "Can you optimize slow database queries?",
          answer: "Yes, we analyze and rewrite queries, suggest indexing, caching, and other DB optimizations."
        },
        {
          id: 37,
          question: "What is your approach to scalable system architecture?",
          answer: "We use microservices, containerization (Docker), auto-scaling, and modular APIs."
        }
      ]
    },
    {
      category: "🛠️ CUSTOMIZATION & DEVELOPMENT",
      questions: [
        {
          id: 38,
          question: "Can you customize your AI agents or APIs for my business?",
          answer: "Yes, all solutions are customizable based on your workflow and logic."
        },
        {
          id: 39,
          question: "How do you handle feature requests?",
          answer: "We prioritize them based on business impact and can include them in the roadmap for premium plans."
        },
        {
          id: 40,
          question: "Can you build from scratch or just optimize existing solutions?",
          answer: "We can do both—build from scratch or enhance and scale existing setups."
        },
        {
          id: 41,
          question: "What is the development process like?",
          answer: "We follow agile sprints with weekly updates and reviews."
        },
        {
          id: 42,
          question: "Do you use agile or waterfall development?",
          answer: "Primarily agile, but can adapt based on client needs."
        },
        {
          id: 43,
          question: "How often will I receive updates during development?",
          answer: "We provide weekly progress reports or as agreed upon."
        },
        {
          id: 44,
          question: "Will I own the source code?",
          answer: "Yes, in most projects, you retain full ownership after delivery."
        }
      ]
    },
    {
      category: "💰 PRICING & PLANS",
      questions: [
        {
          id: 45,
          question: "How is pricing structured?",
          answer: "Pricing is flexible and depends on the services you choose — whether AI agents, API development, automation, or full-stack solutions. We offer monthly plans, per-project billing, and custom quotes."
        },
        {
          id: 46,
          question: "Do you charge per user, per API call, or per project?",
          answer: "It depends on the service: * APIs: per call or per volume * AI agents: per interaction or flat monthly rate * Custom development: per project"
        },
        {
          id: 47,
          question: "Are there any setup fees?",
          answer: "Some enterprise-level solutions or custom integrations may include setup fees. Most small to mid-tier plans have no setup cost."
        },
        {
          id: 48,
          question: "Can I switch plans later?",
          answer: "Yes, plans can be upgraded or downgraded based on your needs at any time."
        },
        {
          id: 49,
          question: "Do you offer discounts for startups or nonprofits?",
          answer: "Yes, we offer special pricing for eligible startups, educational institutions, and nonprofits."
        },
        {
          id: 50,
          question: "What payment methods do you accept?",
          answer: "We accept credit/debit cards, bank transfers, UPI, Stripe, Razorpay, and international payments."
        },
        {
          id: 51,
          question: "Do you offer refunds?",
          answer: "Refunds are considered on a case-by-case basis for valid issues within our terms of service."
        },
        {
          id: 52,
          question: "Is there a pay-as-you-go option?",
          answer: "Yes, we offer usage-based pricing models for API and AI usage with no long-term commitments."
        }
      ]
    },
    {
      category: "📞 SUPPORT & ONBOARDING",
      questions: [
        {
          id: 53,
          question: "What kind of support do you offer?",
          answer: "We offer email, chat, and phone support. Priority support is available on higher-tier plans."
        },
        {
          id: 54,
          question: "What are your support hours?",
          answer: "Standard support is available from 9 AM to 8 PM IST, Monday to Saturday. 24/7 support is available on enterprise plans."
        },
        {
          id: 55,
          question: "Do you offer dedicated account managers?",
          answer: "Yes, enterprise and premium plan users receive a dedicated account manager."
        },
        {
          id: 56,
          question: "How long does onboarding take?",
          answer: "Most onboarding processes are completed within 3–7 days depending on project complexity."
        },
        {
          id: 57,
          question: "Will I get training on how to use your platform?",
          answer: "Yes, we provide one-on-one walkthroughs, documentation, and training videos."
        },
        {
          id: 58,
          question: "Can you help migrate my data?",
          answer: "Yes, we offer data migration support as part of onboarding for most plans."
        },
        {
          id: 59,
          question: "What happens if I need help outside of support hours?",
          answer: "You can raise a ticket or email us anytime. For emergencies, priority customers have access to 24/7 support channels."
        }
      ]
    },
    {
      category: "🔒 SECURITY & COMPLIANCE",
      questions: [
        {
          id: 60,
          question: "Is my data secure?",
          answer: "Yes. We implement industry-standard encryption, secure access control, and follow best practices for data protection."
        },
        {
          id: 61,
          question: "Do you store sensitive customer information?",
          answer: "Only as needed for service delivery, and always with encryption and access restrictions."
        },
        {
          id: 62,
          question: "What encryption methods do you use?",
          answer: "We use HTTPS, TLS 1.2+, and AES‑256 encryption for data in transit and at rest."
        },
        {
          id: 63,
          question: "Are you GDPR or HIPAA compliant?",
          answer: "We follow GDPR best practices and can align with HIPAA requirements for healthcare clients."
        },
        {
          id: 64,
          question: "How do you handle user authentication and access control?",
          answer: "We use secure authentication mechanisms including OAuth, JWT, and role-based access control (RBAC)."
        },
        {
          id: 65,
          question: "Can I host the solution on my own servers?",
          answer: "Yes, for enterprise clients we offer on-premise or hybrid deployment options."
        },
        {
          id: 66,
          question: "Do you sign NDAs or contracts?",
          answer: "Yes, we can sign NDAs, MSAs, and project-based contracts for all clients."
        }
      ]
    },
    {
      category: "🚀 DEPLOYMENT & PERFORMANCE",
      questions: [
        {
          id: 67,
          question: "How long does it take to go live?",
          answer: "It depends on the project scope. Simple integrations can go live in days, while full implementations take 2–4 weeks."
        },
        {
          id: 68,
          question: "Do you offer uptime guarantees or SLAs?",
          answer: "Yes. Enterprise plans come with 99.9% uptime SLAs and performance monitoring."
        },
        {
          id: 69,
          question: "Can your solutions handle enterprise-level scale?",
          answer: "Absolutely. We've built and scaled systems handling millions of API calls and high-volume AI interactions."
        },
        {
          id: 70,
          question: "What happens if there's a performance issue?",
          answer: "Our monitoring tools alert us in real time, and we take corrective action immediately. Clients can report issues through priority channels."
        },
        {
          id: 71,
          question: "Will the AI or API work during high traffic loads?",
          answer: "Yes, our systems are load-tested and built for scalability using cloud-native technologies."
        },
        {
          id: 72,
          question: "How do you monitor and debug performance?",
          answer: "We use tools like Grafana, Prometheus, Datadog, and custom dashboards to monitor performance and trigger alerts."
        }
      ]
    },
    {
      category: "🔁 INTEGRATIONS",
      questions: [
        {
          id: 74,
          question: "Can I connect with WhatsApp, Slack, or other messaging apps?",
          answer: "Yes, our AI agents and APIs support WhatsApp Business, Slack, Telegram, Discord, and more."
        },
        {
          id: 75,
          question: "Do you support Zapier or other automation tools?",
          answer: "Yes, we support Zapier, Make.com (Integromat), and custom webhook integrations."
        },
        {
          id: 76,
          question: "Can I connect your APIs to my mobile app?",
          answer: "Yes, our APIs are mobile-ready and support both Android and iOS integration."
        },
        {
          id: 77,
          question: "What databases or CRMs do you support?",
          answer: "We support MongoDB, PostgreSQL, MySQL, Airtable, Salesforce, Zoho, HubSpot, and more."
        },
        {
          id: 78,
          question: "Can you create custom connectors for rare tools?",
          answer: "Yes, we can build custom integrations for most platforms with open APIs."
        }
      ]
    },
    {
      category: "📊 ANALYTICS & MONITORING",
      questions: [
        {
          id: 79,
          question: "Do your agents come with performance analytics?",
          answer: "Yes, you get detailed analytics on conversations, usage, resolution time, and more."
        },
        {
          id: 80,
          question: "Can I track API usage in real time?",
          answer: "Yes, dashboards and logs let you monitor API calls, latency, and errors in real-time."
        },
        {
          id: 81,
          question: "Do you offer dashboards or reports?",
          answer: "Yes, all plans include access to analytics dashboards. Custom reports are available."
        },
        {
          id: 82,
          question: "How can I evaluate the ROI of your solutions?",
          answer: "We help you track time saved, leads generated, response time reduction, and other KPIs."
        }
      ]
    },
    {
      category: "📚 DOCUMENTATION & TRAINING",
      questions: [
        {
          id: 83,
          question: "Is there documentation available?",
          answer: "Yes, detailed technical and user documentation is available for all services."
        },
        {
          id: 84,
          question: "Do you provide onboarding manuals?",
          answer: "Yes, onboarding guides and checklists are shared at the start of every project."
        },
        {
          id: 86,
          question: "Can my team be trained on the admin panel?",
          answer: "Yes, we offer team training sessions for using dashboards and admin tools."
        },
        {
          id: 87,
          question: "Do you provide changelogs and release notes?",
          answer: "Yes, all updates come with version notes and logs."
        }
      ]
    },
    {
      category: "👥 TEAMS & COLLABORATION",
      questions: [
        {
          id: 88,
          question: "Can multiple users access and manage the dashboard?",
          answer: "Yes, you can add team members with specific access rights."
        },
        {
          id: 89,
          question: "Can I assign different roles/permissions to my team?",
          answer: "Yes, we support role-based access control (admin, editor, viewer, etc.)."
        },
        {
          id: 90,
          question: "Do you support multi-team or multi-location setups?",
          answer: "Yes, our platform supports multiple teams, branches, and access segmentation."
        }
      ]
    },
    {
      category: "📅 CONTRACTS & TERMS",
      questions: [
        {
          id: 91,
          question: "Is there a minimum contract period?",
          answer: "No, most services can be used monthly. Custom projects may have minimum commitments."
        },
        {
          id: 93,
          question: "How is my data handled after cancellation?",
          answer: "Data is archived securely and deleted permanently upon request, in compliance with privacy laws."
        },
        {
          id: 94,
          question: "Do I need to sign a contract or can I start immediately?",
          answer: "You can start with simple terms or request a formal agreement before beginning."
        }
      ]
    },
    {
      category: "🌍 INTERNATIONALIZATION",
      questions: [
        {
          id: 96,
          question: "Do you support clients outside India?",
          answer: "Yes, we work with clients in the US, UK, UAE, Australia, and more."
        },
        {
          id: 97,
          question: "Can your AI agents support local languages?",
          answer: "Yes, we support multiple languages including Hindi, Tamil, Spanish, French, etc."
        },
        {
          id: 98,
          question: "Can I get invoices in my local currency?",
          answer: "Yes, we offer invoicing in INR, USD, AED, EUR and other currencies."
        }
      ]
    },
    {
      category: "🆘 TROUBLESHOOTING",
      questions: [
        {
          id: 99,
          question: "What should I do if something isn't working?",
          answer: "First, check the documentation. If the issue persists, raise a ticket or contact support."
        },
        {
          id: 100,
          question: "How quickly do you respond to support tickets?",
          answer: "Standard response time is within 2–4 business hours. Priority support is faster."
        },
        {
          id: 101,
          question: "Can I escalate an issue if it's urgent?",
          answer: "Yes, high-priority issues can be escalated directly to technical leads or account managers."
        }
      ]
    }
  ];

  const filteredData = useMemo(() => {
    if (!searchTerm.trim()) return faqData;
    
    return faqData.map(category => ({
      ...category,
      questions: category.questions.filter(question => 
        question.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
        question.answer.toLowerCase().includes(searchTerm.toLowerCase())
      )
    })).filter(category => category.questions.length > 0);
  }, [searchTerm]);

  const toggleExpanded = (questionId) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(questionId)) {
      newExpanded.delete(questionId);
    } else {
      newExpanded.add(questionId);
    }
    setExpandedItems(newExpanded);
  };

  const expandAll = () => {
    const allQuestionIds = new Set();
    faqData.forEach(category => {
      category.questions.forEach(question => {
        allQuestionIds.add(question.id);
      });
    });
    setExpandedItems(allQuestionIds);
  };

  const collapseAll = () => {
    setExpandedItems(new Set());
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-6 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h1>
          <p className="text-lg text-gray-600 mb-8">
            Find answers to common questions about our AI agents, API services, and technical solutions.
          </p>
          
          {/* Search Bar */}
          <div className="relative max-w-2xl mx-auto mb-8">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <i className="ri-search-line text-gray-400"></i>
            </div>
            <input
              type="text"
              placeholder="Search FAQs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex justify-center gap-4 mb-8">
            <button
              onClick={expandAll}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Expand All
            </button>
            <button
              onClick={collapseAll}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Collapse All
            </button>
          </div>
        </div>

        {/* FAQ Content */}
        <div className="space-y-8">
          {filteredData.map((category, categoryIndex) => (
            <div key={categoryIndex} className="bg-white rounded-lg shadow-sm">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">{category.category}</h2>
              </div>
              <div className="divide-y divide-gray-200">
                {category.questions.map((question) => (
                  <div key={question.id} className="p-6">
                    <button
                      onClick={() => toggleExpanded(question.id)}
                      className="w-full text-left flex justify-between items-center hover:bg-gray-50 p-2 rounded-lg transition-colors"
                    >
                      <h3 className="text-lg font-medium text-gray-900 pr-4">
                        {question.question}
                      </h3>
                      <i className={`ri-arrow-down-s-line text-gray-500 transition-transform ${
                        expandedItems.has(question.id) ? 'rotate-180' : ''
                      }`}></i>
                    </button>
                    {expandedItems.has(question.id) && (
                      <div className="mt-4 pl-2">
                        <p className="text-gray-700 leading-relaxed">
                          {question.answer}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* No Results */}
        {filteredData.length === 0 && searchTerm && (
          <div className="text-center py-12">
            <i className="ri-search-line text-4xl text-gray-400 mb-4"></i>
            <h3 className="text-xl font-medium text-gray-900 mb-2">No results found</h3>
            <p className="text-gray-600">
              Try searching with different keywords or browse all categories.
            </p>
          </div>
        )}

        {/* Contact CTA */}
        <div className="mt-16 bg-blue-50 rounded-lg p-8 text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">Still have questions?</h3>
          <p className="text-gray-600 mb-6">
            Can't find what you're looking for? Our team is here to help.
          </p>
          <a
            href="mailto:contactus@krishnaapihub.com"
            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <i className="ri-mail-line mr-2"></i>
            Contact Us
          </a>
        </div>
      </div>
    </div>
  );
};

export default FAQ;
